// Contacts: directory entry state and session state with each company's server.

import { deleteDeviceKey } from '$lib/crypto/device-keys';
import type { DirectoryEntry } from '$lib/protocol/types';

import { db, type ContactRow } from './db';
import { purgeMediaForAccount } from './media-store';

export function allContacts(): Promise<ContactRow[]> {
  return db.contacts.toArray();
}

export function contactById(accountId: string): Promise<ContactRow | undefined> {
  return db.contacts.get(accountId);
}

export function contactByNumber(phone: string): Promise<ContactRow | undefined> {
  return db.contacts.filter((c) => c.phone === phone).first();
}

/**
 * Stores what the directory says about a company.
 *
 * The number comes from the entry only when this client derived it — otherwise
 * from the row already held, because it is the user's own record of what they
 * typed. The trading name is not here at all: it arrives from the company's own
 * server on sign-in, through [saveDistributorName].
 *
 * The client pins the server key on first connection. If the key changes, sending
 * is paused — the consequence has to be that something stops, not merely a
 * warning, because a warning that can be dismissed is not a safeguard.
 */
export async function saveFromEntry(entry: DirectoryEntry): Promise<void> {
  const existing = await db.contacts.get(entry.accountId);
  const phone = entry.phone ?? existing?.phone;
  if (phone === undefined) {
    // Nothing to file this under. A contact exists because somebody typed a number.
    return;
  }
  const keyChanged =
    existing !== undefined &&
    existing.pinnedServerKey.length > 0 &&
    existing.pinnedServerKey !== entry.serverKey;

  const row: ContactRow = {
    accountId: entry.accountId,
    entryKey: entry.entryKey,
    phone,
    name: existing?.name ?? '',
    serverUrl: entry.serverUrl,
    serverKey: entry.serverKey,
    pinnedServerKey: existing?.pinnedServerKey ?? entry.serverKey,
    status: entry.status,
    successorKey: entry.successorKey ?? null,
    successorAccountId: entry.successorAccountId ?? null,
    successorSigned: entry.successorSigned,
    changePendingUntilMs: entry.changePendingUntil?.getTime() ?? null,
    sendingPaused: keyChanged || entry.status === 'change_pending',
    needsReverify: existing?.needsReverify ?? false,
    threadId: existing?.threadId ?? null,
    deviceId: existing?.deviceId ?? null,
    token: existing?.token ?? null,
    tokenExpiresMs: existing?.tokenExpiresMs ?? null,
    quarantineUntilMs: existing?.quarantineUntilMs ?? null,
    quarantineRestrictions: existing?.quarantineRestrictions ?? [],
    cursor: existing?.cursor ?? null,
    lastSequence: existing?.lastSequence ?? 0,
    notificationKeyB64: existing?.notificationKeyB64 ?? null,
    viewsVersion: existing?.viewsVersion ?? null,
    addedAtMs: existing?.addedAtMs ?? Date.now(),
    unreadCount: existing?.unreadCount ?? 0,
    pendingCount: existing?.pendingCount ?? 0,
    lastEventTimeMs: existing?.lastEventTimeMs ?? null,
    lastEventSequence: existing?.lastEventSequence ?? 0,
    lastEventJson: existing?.lastEventJson ?? null,
    lastFromUser: existing?.lastFromUser ?? false,
    lastOutboxTimeMs: existing?.lastOutboxTimeMs ?? null,
  };
  await db.contacts.put(row);
}

/**
 * Follows a SIGNED move: the conversation history stays, everything tied to the
 * old server is dropped. The successor runs a different server with its own
 * sign-in, so the session, the cursor and the thread id are cleared — the next
 * send walks the verification path again. The user is not asked to confirm any of
 * this, because their consent verifies nothing; the predecessor's signature did.
 */
export async function migrateToSuccessor(
  oldAccountId: string,
  successor: DirectoryEntry,
): Promise<void> {
  const old = await db.contacts.get(oldAccountId);
  if (old === undefined || oldAccountId === successor.accountId) {
    return;
  }
  await db.transaction(
    'rw',
    [db.contacts, db.events, db.outbox, db.mutes, db.calendarEntries, db.mediaCache],
    async () => {
      await db.events.where('[accountId+sequence]')
        .between([oldAccountId, -Infinity], [oldAccountId, Infinity])
        .modify({ accountId: successor.accountId });
      await db.outbox.where('accountId').equals(oldAccountId).modify({ accountId: successor.accountId });
      const mutes = await db.mutes.where('[accountId+category]')
        .between([oldAccountId, ''], [oldAccountId, '￿'])
        .toArray();
      for (const mute of mutes) {
        await db.mutes.delete([mute.accountId, mute.category]);
        await db.mutes.put({ accountId: successor.accountId, category: mute.category });
      }
      const calendar = await db.calendarEntries.where('accountId').equals(oldAccountId).toArray();
      for (const row of calendar) {
        await db.calendarEntries.delete(row.id);
        await db.calendarEntries.put({
          ...row,
          id: `${successor.accountId}/${row.eventId}`,
          accountId: successor.accountId,
        });
      }
      await db.mediaCache.where('accountId').equals(oldAccountId).modify({ accountId: successor.accountId });
      await db.contacts.delete(oldAccountId);
      await db.contacts.put({
        accountId: successor.accountId,
        entryKey: successor.entryKey,
        // The number the user typed, carried over. Following a move never teaches
        // this client the successor's own number — the file names them by key.
        phone: successor.phone ?? old.phone,
        name: old.name,
        serverUrl: successor.serverUrl,
        serverKey: successor.serverKey,
        pinnedServerKey: successor.serverKey,
        status: successor.status,
        successorKey: null,
        successorAccountId: null,
        successorSigned: false,
        changePendingUntilMs: null,
        sendingPaused: false,
        needsReverify: false,
        threadId: null,
        deviceId: null,
        token: null,
        tokenExpiresMs: null,
        quarantineUntilMs: null,
        quarantineRestrictions: [],
        cursor: null,
        lastSequence: 0,
        // A new relation gets a new notification key — the successor is a
        // different server, and key reuse across relations is what the
        // per-relation design exists to prevent.
        notificationKeyB64: null,
        // A move is a different server, so the page list starts unknown and the first
        // sync fetches it — the same reasoning as the notification key above.
        viewsVersion: null,
        addedAtMs: old.addedAtMs,
        unreadCount: old.unreadCount,
        pendingCount: old.pendingCount,
        lastEventTimeMs: old.lastEventTimeMs,
        lastEventSequence: old.lastEventSequence,
        lastEventJson: old.lastEventJson,
        lastFromUser: old.lastFromUser,
        lastOutboxTimeMs: old.lastOutboxTimeMs,
      });
    },
  );
}

/** The trading name, as the company's own server reports it on sign-in. Its only
 *  job is making the conversation list readable — security never rested on it. */
export async function saveDistributorName(accountId: string, name: string | undefined): Promise<void> {
  if (name === undefined || name.length === 0) {
    return;
  }
  await db.contacts.update(accountId, { name });
}

export async function saveSession(input: {
  accountId: string;
  token: string;
  expiresAtMs: number;
  threadId: string;
  deviceId?: string;
  quarantineUntilMs: number | undefined;
}): Promise<void> {
  await db.contacts.update(input.accountId, {
    token: input.token,
    tokenExpiresMs: input.expiresAtMs,
    threadId: input.threadId,
    ...(input.deviceId !== undefined ? { deviceId: input.deviceId } : {}),
    quarantineUntilMs: input.quarantineUntilMs ?? null,
    needsReverify: false,
  });
}

/** Where the client got to in the event stream. */
export async function saveCursor(
  accountId: string,
  cursor: string | undefined,
  lastSequence: number,
): Promise<void> {
  await db.contacts.update(accountId, { cursor: cursor ?? null, lastSequence });
}

export async function markNeedsReverify(accountId: string): Promise<void> {
  await db.contacts.update(accountId, { needsReverify: true });
}

/** The user explicitly trusts the changed server key; sending resumes. */
export async function resumeSending(accountId: string, newKey: string): Promise<void> {
  await db.contacts.update(accountId, { pinnedServerKey: newKey, sendingPaused: false });
}

/** Full local wipe of a contact — events, queue, media, calendar, mutes and the
 *  device key. The server-side DELETE is the caller's (best-effort) job. */
export async function removeContactLocally(accountId: string): Promise<void> {
  await db.transaction(
    'rw',
    [db.contacts, db.events, db.outbox, db.pendingMedia, db.mutes, db.calendarEntries, db.mediaCache],
    async () => {
      const queued = await db.outbox.where('accountId').equals(accountId).toArray();
      for (const item of queued) {
        if (item.pendingMediaId !== null) {
          await db.pendingMedia.delete(item.pendingMediaId);
        }
      }
      await db.outbox.where('accountId').equals(accountId).delete();
      await db.events.where('[accountId+sequence]')
        .between([accountId, -Infinity], [accountId, Infinity])
        .delete();
      await db.mutes.where('[accountId+category]')
        .between([accountId, ''], [accountId, '￿'])
        .delete();
      await db.calendarEntries.where('accountId').equals(accountId).delete();
      await db.contacts.delete(accountId);
    },
  );
  await purgeMediaForAccount(accountId);
  await deleteDeviceKey(accountId);
}

// ——— mutes ———
//
// Display muting is local: a muted notification still arrives and renders
// collapsed, and no read state betrays it. The one deliberate disclosure is the
// wake preference — when push is enabled, the current category list rides along
// with capability renewal (`muted_categories`) so the server does not wake this
// device for them at all.

export async function mutedCategories(accountId: string): Promise<Set<string>> {
  const rows = await db.mutes.where('[accountId+category]')
    .between([accountId, ''], [accountId, '￿'])
    .toArray();
  return new Set(rows.map((r) => r.category));
}

export async function setMute(accountId: string, category: string, muted: boolean): Promise<void> {
  if (muted) {
    await db.mutes.put({ accountId, category });
  } else {
    await db.mutes.delete([accountId, category]);
  }
}
